import { Suspense, useCallback, useEffect } from "react";
import { useIntersectionObserver } from "@/shared/hooks/useIntersectionObserver.ts";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import {
  useTaskCollectionList,
  useTaskList,
} from "@/pages/get-don/tasks.api.ts";
import TaskCard, { CollectionTaskCard } from "./TaskCard.tsx";
import TaskActionButton from "@/pages/get-don/task-actions/TestActionButton.tsx";
import { useCategoryStore } from "@/pages/get-don/store/category.store.ts";
import TaskLoading from "@/pages/get-don/components/cards/GetDonCardLoading.tsx";
import NoData from "../NoData.tsx";
import { useTranslation } from "react-i18next";

interface TaskListProps {}

const TasksList = (props: TaskListProps) => {
  return (
    <Suspense
      fallback={
        <div className="grid gap-1">
          {Array.from({ length: 3 }).map((_, idx) => (
            <TaskLoading key={idx} />
          ))}
        </div>
      }
    >
      <BaseTasksList {...props} />
    </Suspense>
  );
};

const BaseTasksList = () => {
  const { t } = useTranslation();
  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.1,
  });
  const { selectedCategory } = useCategoryStore();

  const {
    data: assignment,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useSuspenseInfiniteQuery(
    useTaskList({
      selectedCategory: selectedCategory.id,
      enabled: !selectedCategory.isCollection,
    })
  );

  const {
    data: taskCollection,
    fetchNextPage: fetchNextPageCollection,
    hasNextPage: hasNextPageCollection,
    isFetchingNextPage: isFetchingNextPageCollection,
  } = useSuspenseInfiniteQuery(
    useTaskCollectionList(
      selectedCategory.isCollection
        ? selectedCategory.id ?? undefined
        : undefined
    )
  );

  // Fetch next page when the loader element is visible
  useEffect(() => {
    if (isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [isIntersecting, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (
      isIntersecting &&
      hasNextPageCollection &&
      !isFetchingNextPageCollection
    ) {
      fetchNextPageCollection();
    }
  }, [
    isIntersecting,
    hasNextPageCollection,
    isFetchingNextPageCollection,
    fetchNextPageCollection,
  ]);

  const tasksList = assignment.pages.flatMap((page) => page.results);
  const taskCollectionList = taskCollection.pages.flatMap(
    (page) => page.results
  );

  const renderTaskCard = useCallback(() => {
    if (selectedCategory.isCollection) {
      return taskCollectionList.map((item, index) => (
        <CollectionTaskCard key={index} {...item} />
      ));
    }

    return tasksList.map((item, index) => (
      <TaskCard
        key={index}
        {...item}
        actionComponent={
          <TaskActionButton
            task={item}
            selectedCategoryId={selectedCategory.id}
          />
        }
      />
    ));
  }, [selectedCategory, taskCollectionList, tasksList]);

  if (selectedCategory.isCollection) {
    if (!taskCollectionList.length) {
      return (
        <NoData
          image={<img src="images/common-no-data.png" className="size-24" />}
          title={t("no_data_title")}
          description={t("no_data_subtitle")}
          headerClassName="bg-none p-0 m-0"
        />
      );
    }
  } else if (!tasksList.length) {
    return (
      <NoData
        image={<img src="images/common-no-data.png" className="size-24" />}
        title={t("no_data_title")}
        description={t("no_data_subtitle")}
        headerClassName="bg-none p-0 m-0"
      />
    );
  }

  return (
    <div className="flex flex-col overflow-hidden">
      {renderTaskCard()}
      {(hasNextPage ||
        isFetchingNextPage ||
        hasNextPageCollection ||
        isFetchingNextPageCollection) && (
        <div ref={ref}>
          {Array.from({ length: 1 }).map((_, idx) => (
            <TaskLoading key={idx} />
          ))}
        </div>
      )}
    </div>
  );
};

export default TasksList;
